import { useEffect, useCallback, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getInterviewConfigList } from "../../application/services/interviewConfigService";
import { setInterviewList } from "../../store/interviewConfigReducer";

const LAST_FETCHED_STATUS = { current: Symbol("never") };

const useInterviewList = ({ status } = {}) => {
  const dispatch = useDispatch();
  const { interviewList } = useSelector((state) => state.interviewconfig);
  const interviewListRef = useRef(interviewList);
  const loadingRef = useRef(false);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [currentFilters, setCurrentFilters] = useState({});

  interviewListRef.current = interviewList;
  loadingRef.current = loading;

  const fetchInterviewList = useCallback(
    async (pageNum = 1, append = false, filters = {}) => {
      if (loadingRef.current) return;
      setLoading(true);
      loadingRef.current = true;

      if (!append) {
        setCurrentFilters(filters);
        setPage(1);
      }

      try {
        const response = await getInterviewConfigList(
          pageNum,
          10,
          "created_at",
          "DESC",
          filters,
          status
        );
        const newData = response?.data || [];

        const updatedList = append
          ? [...(interviewListRef.current || []), ...newData]
          : newData;

        dispatch(setInterviewList(updatedList));
        LAST_FETCHED_STATUS.current = status;

        if (newData.length < 10) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }
      } catch (error) {
        console.error("Error fetching interview list:", error);
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    },
    [dispatch, status, currentFilters]
  );

  const loadMore = useCallback(() => {
    if (hasMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchInterviewList(nextPage, true, currentFilters);
    }
  }, [hasMore, loading, page, fetchInterviewList, currentFilters]);

  useEffect(() => {
    const statusMatches = LAST_FETCHED_STATUS.current === status;
    if (!statusMatches) {
      fetchInterviewList(1, false);
    }
  }, [fetchInterviewList, status]);

  return { interviewList, loadMore, hasMore, loading, fetchInterviewList };
};

export default useInterviewList;
